/**
 * features/credit/creditNotesWrite.ts
 * A credit note reverses or corrects an issued invoice. It gets its
 * own gapless CRN number on issue and records an audit reason.
 */
import { collection, deleteDoc, doc } from 'firebase/firestore';
import { db, addDoc, updateDoc } from '@/lib/firebase';
import { nextNumber } from '@/lib/numbering';
import {
  COLLECTIONS,
  type ClientSnapshot,
  type DocumentTotals,
  type LineItem,
  type VatSnapshot,
} from '@/types';

export interface CreditNotePayload {
  invoiceId: string;
  invoiceNumber: string;
  clientId: string;
  client: ClientSnapshot;
  issueDate: string;
  reason: string;
  lineItems: LineItem[];
  totals: DocumentTotals;
  vat: VatSnapshot;
}

function baseDoc(p: CreditNotePayload) {
  return {
    invoiceId: p.invoiceId,
    invoiceNumber: p.invoiceNumber,
    clientId: p.clientId,
    client: p.client,
    issueDate: p.issueDate,
    reason: p.reason.trim(),
    lineItems: p.lineItems,
    totals: p.totals,
    currency: 'BWP' as const,
    vat: p.vat,
    updatedAt: new Date().toISOString(),
  };
}

export async function saveDraft(p: CreditNotePayload, actor: string, id?: string): Promise<string> {
  const data = { ...baseDoc(p), number: null, status: 'draft' as const };
  if (id) {
    await updateDoc(doc(db, COLLECTIONS.creditNotes, id), data);
    return id;
  }
  const ref = await addDoc(collection(db, COLLECTIONS.creditNotes), {
    ...data,
    createdAt: new Date().toISOString(),
    createdBy: actor,
  });
  return ref.id;
}

export async function issueCreditNote(p: CreditNotePayload, actor: string, id?: string): Promise<{ id: string; number: string }> {
  const number = await nextNumber('creditNote', new Date(p.issueDate));
  const now = new Date().toISOString();
  const data = { ...baseDoc(p), number, status: 'issued' as const, issuedAt: now };
  if (id) {
    await updateDoc(doc(db, COLLECTIONS.creditNotes, id), data);
    return { id, number };
  }
  const ref = await addDoc(collection(db, COLLECTIONS.creditNotes), {
    ...data,
    createdAt: now,
    createdBy: actor,
  });
  return { id: ref.id, number };
}

export async function voidCreditNote(id: string): Promise<void> {
  await updateDoc(doc(db, COLLECTIONS.creditNotes, id), { status: 'void', updatedAt: new Date().toISOString() });
}

export async function deleteDraft(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTIONS.creditNotes, id));
}
